/**
 * Google Map Tiles API (2D tiles) session tokens.
 *
 * A token is valid for about two weeks and may be shared by any number of clients, so one token is
 * kept per API key + session request, in memory and in `localStorage`, and replaced a day before it
 * expires. See https://developers.google.com/maps/documentation/tile/session_tokens
 */

export const GOOGLE_MAP_TILES_ORIGIN = 'https://tile.googleapis.com'

export type GoogleMapTilesSessionRequest = {
  mapType: 'roadmap' | 'satellite' | 'terrain'
  language: string
  region: string
  layerTypes?: Array<'layerRoadmap' | 'layerStreetview' | 'layerTraffic'>
  scale?: 'scaleFactor1x' | 'scaleFactor2x' | 'scaleFactor4x'
  highDpi?: boolean
  imageFormat?: 'jpeg' | 'png'
}

export type GoogleMapTilesSession = {
  token: string
  /** Epoch milliseconds. */
  expiresAt: number
}

export type GoogleMapTilesSessionStore = {
  /** A usable cached session, without network. `null` when none or expired. */
  current(): GoogleMapTilesSession | null
  /** Resolves a usable session, creating one when needed; refreshes ahead of expiry. */
  ensure(): Promise<GoogleMapTilesSession>
}

export type GoogleMapTilesSessionStoreOptions = {
  apiKey: string
  request: GoogleMapTilesSessionRequest
  fetch?: typeof fetch
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  now?: () => number
}

const STORAGE_PREFIX = 'gpx.google-map-tiles.session.v1:'
/** Replace the token this long before Google's expiry. */
export const GOOGLE_MAP_TILES_REFRESH_MARGIN_MS = 24 * 60 * 60 * 1000

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function storageKey(apiKey: string, request: GoogleMapTilesSessionRequest): string {
  return STORAGE_PREFIX + apiKey.slice(-8) + ':' + JSON.stringify(request)
}

function parseStored(raw: string | null): GoogleMapTilesSession | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Partial<GoogleMapTilesSession>
    if (typeof value.token !== 'string' || typeof value.expiresAt !== 'number') return null
    return { token: value.token, expiresAt: value.expiresAt }
  } catch {
    return null
  }
}

export async function createGoogleMapTilesSession(
  apiKey: string,
  request: GoogleMapTilesSessionRequest,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleMapTilesSession> {
  const res = await fetchImpl(
    `${GOOGLE_MAP_TILES_ORIGIN}/v1/createSession?key=${encodeURIComponent(apiKey)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
  )
  if (!res.ok) {
    throw new Error(`Google Map Tiles createSession failed: HTTP ${res.status}`)
  }
  const body = (await res.json()) as { session?: string; expiry?: string }
  const expirySeconds = Number(body.expiry)
  if (!body.session || !Number.isFinite(expirySeconds)) {
    throw new Error('Google Map Tiles createSession returned no session')
  }
  return { token: body.session, expiresAt: expirySeconds * 1000 }
}

export function createGoogleMapTilesSessionStore(
  options: GoogleMapTilesSessionStoreOptions,
): GoogleMapTilesSessionStore {
  const fetchImpl = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args))
  const storage = options.storage === undefined ? defaultStorage() : options.storage
  const now = options.now ?? Date.now
  const key = storageKey(options.apiKey, options.request)

  let session: GoogleMapTilesSession | null = parseStored(safeGet())
  let inFlight: Promise<GoogleMapTilesSession> | null = null

  function safeGet(): string | null {
    try {
      return storage?.getItem(key) ?? null
    } catch {
      return null
    }
  }

  function save(next: GoogleMapTilesSession) {
    session = next
    try {
      storage?.setItem(key, JSON.stringify(next))
    } catch {
      // Storage full or blocked: the in-memory copy still serves this page.
    }
  }

  function usable(s: GoogleMapTilesSession | null): s is GoogleMapTilesSession {
    return s !== null && s.expiresAt > now()
  }

  function fresh(s: GoogleMapTilesSession | null): s is GoogleMapTilesSession {
    return s !== null && s.expiresAt - now() > GOOGLE_MAP_TILES_REFRESH_MARGIN_MS
  }

  function refresh(): Promise<GoogleMapTilesSession> {
    if (!inFlight) {
      inFlight = createGoogleMapTilesSession(options.apiKey, options.request, fetchImpl)
        .then((next) => {
          save(next)
          return next
        })
        .finally(() => {
          inFlight = null
        })
    }
    return inFlight
  }

  function current(): GoogleMapTilesSession | null {
    if (!usable(session)) {
      // Another tab may have refreshed it.
      session = parseStored(safeGet())
    }
    return usable(session) ? session : null
  }

  return {
    current,
    ensure() {
      const cached = current()
      if (fresh(cached)) return Promise.resolve(cached)
      if (cached) {
        refresh().catch(() => {})
        return Promise.resolve(cached)
      }
      return refresh()
    },
  }
}

const sharedStores = new Map<string, GoogleMapTilesSessionStore>()

/** One store per API key + request for the whole page, so every map shares one token. */
export function getSharedGoogleMapTilesSessionStore(
  apiKey: string,
  request: GoogleMapTilesSessionRequest,
): GoogleMapTilesSessionStore {
  const key = storageKey(apiKey, request)
  let store = sharedStores.get(key)
  if (!store) {
    store = createGoogleMapTilesSessionStore({ apiKey, request })
    sharedStores.set(key, store)
  }
  return store
}
